import React from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { roundNumber, roundToTwoDecimals } from "utils/math";

export interface DataChart {
  name: string;
  value: number;
  quartiles: number[];
  colorBar: string;
  opacityColorBar: string;
}

interface PlotChartProps {
  data: DataChart;
  dataTicks: number[];
  isMissingDataRequirements: boolean;
}

const renderTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const bestEstimate = roundToTwoDecimals(payload[0].payload.value);
    return (
      <div className="bg-gray-600 flex flex-col gap-1 p-2 text-white rounded-md text-xs">
        <p className="font-bold">
          Best estimate: <span className="font-normal">{bestEstimate > 99.99 ? 99.99 : bestEstimate}%</span>
        </p>
        {!!payload[0].payload?.quartiles && payload[0].payload.quartiles.length > 0 && (
          <p className="font-bold">
            Confident range:{" "}
            <span className="font-normal">{`${roundToTwoDecimals(
              payload[0].payload.quartiles[0]
            )}% - ${roundToTwoDecimals(payload[0].payload.quartiles[1])}%`}</span>
          </p>
        )}
      </div>
    );
  }

  return null;
};

const renderCustomizedLabel = (props: any) => {
  const { x, y, value, color } = props;

  const calcRectX = (value: number, x: number) => {
    if (value === 99) {
      return x - 35;
    }
    if (value === 100) {
      return x - 46;
    }
    return x - 25;
  };

  const calcTextX = (value: number, x: number) => {
    if (value === 99) {
      return x - 5;
    }
    if (value === 100) {
      return x - 15;
    }
    if (value === 0 || value === 1) {
      return x + 16;
    }
    return x + 6;
  };

  return (
    <g>
      <rect x={calcRectX(value, x)} y={y / 2 + 2} width={60} height={30} fill={color} rx="16" />
      <text
        x={calcTextX(value, x)}
        y={y + 5}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="middle"
        className="font-bold text-xl"
      >
        {roundNumber(value > 99 ? 99 : value)}%
      </text>
    </g>
  );
};

export const PlotChart = React.memo((props: PlotChartProps) => {
  const { data, dataTicks, isMissingDataRequirements } = props;

  return (
    <ResponsiveContainer width="100%" height={60}>
      <ComposedChart layout="vertical" data={[data]} className="[&>svg]:overflow-visible">
        <CartesianGrid horizontalPoints={[30]} />
        <YAxis dataKey="name" type="category" tickLine={false} hide />
        <XAxis
          type="number"
          axisLine={false}
          tickLine={false}
          hide
          tickFormatter={(tick) => `${tick}%`}
          domain={[dataTicks[0], dataTicks[dataTicks.length - 1]]}
          ticks={dataTicks}
          interval={0}
        />

        <Bar dataKey="quartiles" minPointSize={2} barSize={24} fill={data.opacityColorBar} fillOpacity="0.5" />
        {!isMissingDataRequirements && (
          <>
            <Scatter dataKey="value">
              <LabelList dataKey="value" color={data.colorBar} content={renderCustomizedLabel} />
            </Scatter>
            <Tooltip cursor={false} content={renderTooltip} offset={5} isAnimationActive={false} />
          </>
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
});
